import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../../components/ui/card';
import { Progress } from '../../../components/ui/progress';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_MESSAGES = [
  'Crafting your prompt...',
  'Generating image...',
  'Applying brand overlay...',
  'Finishing up...'
];

const ESTIMATED_TIMES = {
  draft: 5,
  standard: 15,
  premium: 30
};

function GenerationProgress({ jobId, qualityTier, onComplete, onError }) {
  const [status, status_setStatus] = useState('processing');
  const [progress, setProgress] = useState(0);
  const [currentMessage, setCurrentMessage] = useState(STATUS_MESSAGES[0]);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(
    ESTIMATED_TIMES[qualityTier] || 15
  );

  const messageIndexRef = useRef(0);
  const pollIntervalRef = useRef(null);
  const messageIntervalRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const pollTimeoutRef = useRef(null);
  const hasCompletedRef = useRef(false);

  // Cleanup intervals and timeouts
  const cleanupTimers = () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (messageIntervalRef.current) clearInterval(messageIntervalRef.current);
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
  };

  // Poll job status
  useEffect(() => {
    const POLL_INTERVAL = 2000; // Poll every 2 seconds
    const POLL_TIMEOUT = 120000; // Stop polling after 120 seconds
    let pollStartTime = Date.now();

    const pollStatus = async () => {
      // Check if we've exceeded the timeout
      if (Date.now() - pollStartTime > POLL_TIMEOUT) {
        cleanupTimers();
        status_setStatus('timeout');
        onError('Generation timeout - job took too long to complete');
        return;
      }

      try {
        const response = await axios.get(`${API}/creative/job/${jobId}`);
        const jobData = response.data;
        const jobStatus = jobData.status;

        // Update progress if available
        if (jobData.progress) {
          setProgress(jobData.progress);
        }

        // Check for completion
        if (jobStatus === 'complete') {
          if (hasCompletedRef.current) return; // Prevent duplicate calls
          hasCompletedRef.current = true;

          status_setStatus('completed');
          cleanupTimers();

          // Pass the result object with both single and batch URLs
          const result = {
            imageUrl: jobData.output_image_url,
            imageUrls: jobData.batch_output_urls || (jobData.output_image_url ? [jobData.output_image_url] : [])
          };
          onComplete(result);
        } else if (jobStatus === 'failed') {
          if (hasCompletedRef.current) return; // Prevent duplicate calls
          hasCompletedRef.current = true;

          status_setStatus('failed');
          cleanupTimers();
          onError(jobData.error_message || 'Generation failed');
        }
      } catch (error) {
        // Only log if not a duplicate call
        if (!hasCompletedRef.current) {
          console.error('Poll error:', error);
          cleanupTimers();
          status_setStatus('error');
          onError('Failed to check job status');
        }
      }
    };

    // Call pollStatus immediately on mount, then start interval
    pollStatus();
    pollIntervalRef.current = setInterval(pollStatus, POLL_INTERVAL);

    // Set timeout to stop polling after 120 seconds
    pollTimeoutRef.current = setTimeout(() => {
      if (!hasCompletedRef.current) {
        cleanupTimers();
        status_setStatus('timeout');
        onError('Generation timeout - job took too long to complete');
      }
    }, POLL_TIMEOUT);

    return cleanupTimers;
  }, [jobId, onComplete, onError]);

  // Cycle status messages
  useEffect(() => {
    messageIntervalRef.current = setInterval(() => {
      messageIndexRef.current = (messageIndexRef.current + 1) % STATUS_MESSAGES.length;
      setCurrentMessage(STATUS_MESSAGES[messageIndexRef.current]);
    }, 2000);

    return () => {
      if (messageIntervalRef.current) clearInterval(messageIntervalRef.current);
    };
  }, []);

  // Update timer
  useEffect(() => {
    timerIntervalRef.current = setInterval(() => {
      setTimeElapsed(prev => prev + 1);
      setEstimatedTimeRemaining(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, []);

  return (
    <Card className="card max-w-2xl">
      <CardContent className="p-8">
        <div className="text-center space-y-6">
          {/* Animated Icon */}
          <div className="flex justify-center">
            <Loader2 className="w-12 h-12 text-[#00d4aa] animate-spin" />
          </div>

          {/* Status Message */}
          <div className="space-y-2">
            <p className="text-lg font-semibold text-white">{currentMessage}</p>
            <p className="text-white/60 text-sm">
              Elapsed: {timeElapsed}s • Est. remaining: {estimatedTimeRemaining}s
            </p>
          </div>

          {/* Progress Bar */}
          <div className="space-y-2">
            <Progress value={progress || 30} className="h-2" />
            <p className="text-xs text-white/40">{progress || 30}% complete</p>
          </div>

          {/* Tips */}
          <div className="bg-white/5 border border-white/10 rounded-lg p-4 text-left">
            <p className="text-xs text-white/60 font-medium mb-2">This usually takes {ESTIMATED_TIMES[qualityTier] || 15} seconds</p>
            <ul className="space-y-1 text-xs text-white/40">
              <li>• Don't close this page</li>
              <li>• Your creative will be saved to history</li>
              <li>• You can generate multiple versions</li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default GenerationProgress;
